//@ts-check
import React, { ReactNode, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { isArray } from 'lodash-es';
import {
  CartData,
  CartItem,
  changeItemQuantity,
  isGiftCardWithRecipient,
  removeLineItems,
  SetLineItemProperties,
  ShopifyCartItem,
  showSingleQuantityAmount,
  useCartData,
} from '../../../../utils/cart';
import { ALO_MOVES_30_GWP, ALO_MOVES_REAL_SKU, INTERNATIONAL_RESTRICTIONS_TAG } from '../../../../utils/constants';
import { UrgencyMessage } from '../../../../templates/product/components/ScarcityMessaging/UrgencyMessage';
import { observer } from 'mobx-react-lite';
import { computeCartGroupData } from '../../../../utils/cart/computeCartGroupData';
import { InitialUrlSearchParams } from '../../../../utils/environment';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { shouldRenderUpsellCarousel, updateCartShippingMessagesWithGeolocation } from './CartImpulseBuy/utils';
import {
  computeCartItemGroups,
  computeDisplayForOptionWithValues,
  formatShopifyPrice,
  getStrikeThroughLinePrice,
  QuantityChangeButtons,
} from './helpers';
import { fireGA4cartViewEvent } from '../../../../utils/gtm-data-layer';
import { addItemToWishlistFromURLparam } from '../../../../templates/_shared/wishlist/wishlistUtils';
import WishlistButtonCart from '../../../../templates/_shared/wishlist/WishlistButtonCart';
import {
  checkIfCustomer,
  checkIfItemCanBeSoldOut,
  downcaseObjectKeys,
  formatDateWithLocaleOptions,
  getOnlineSalePricing,
  heapTrack,
  pluralize,
  preorderDateRangeCopy,
} from 'src/scripts/utils/helpers';
import { wishlistState } from 'src/scripts/templates/_shared/wishlist/wishlistState';
import { ShippingCountryState } from '../../../../utils/shipping';
import { memorizeGiftRemoval } from './GWP/gwpUtils';
import { useCheckIfUserIsInUS, usePromise } from '../../../../templates/_shared/react-hooks';
import { GWPandLoyaltyGeoSettings } from '../../../../globals';
import { Gifts } from './GWP/Gifts';
import { Rewards } from './Loyalty/Rewards';
import { fireHeapRemovalGWPevent } from '../../../../utils/heap-events';
import PurchaseLimitMessaging, {
  extractPurchaseLimitTagData,
} from 'src/scripts/templates/_shared/ui-components/PurchaseLimitItem/PurchaseLimitMessaging';
import RemoveFromCartButton from './RemoveFromCartButton';
import { ApolloProvider } from '@apollo/client';
import { isEligibleForPreorder } from 'src/scripts/templates/product/utils';
import { tagsIncludesTag } from '../../../../utils/shopify';
import { autorun } from 'mobx';
import ReshopMessaging from 'src/scripts/templates/_shared/Reshop';
import { defaultReshopCartText } from 'src/scripts/utils/languages';
import { checkIfInVariation } from 'src/scripts/utils/optimizely';
import { CartApolloClient } from 'src/scripts/utils/cart/client';
import { refundClaimedReward } from './Loyalty/loyaltyUtils';
import ErrorBoundary from 'src/scripts/utils/react/ErrorBoundary';

const updateReshopMessage = (isDomestic: boolean) => {
  // only for Domestic users
  const { enabled, enableCartPDPMessaging } = window.getSiteSettings().reshop;
  const reshopEnabled = enabled && isDomestic && enableCartPDPMessaging;

  if (reshopEnabled) {
    let returnMessage = defaultReshopCartText;
    const { customReturnsMessagingEnabled, returnsMessaging: unsanitizedReturnMessaging } = window?.getSiteSettings();
    const returnsMessaging = downcaseObjectKeys(unsanitizedReturnMessaging) as ReturnsMessagingJSON;

    if (customReturnsMessagingEnabled) {
      // reshopCart should be configured in shop.metafields.returns.messaging
      returnMessage = returnsMessaging['us']?.reshopCart || defaultReshopCartText;
    }

    const node = document.querySelector('#cart-sidebar-reshop-copy');
    if (node) {
      ReactDOM.render(
        <p>
          {returnMessage} <ReshopMessaging location="cart" />
        </p>,
        node
      );
    }
  }
};

const updateCartShippingMessages = (isDomestic: boolean) => {
  updateCartShippingMessagesWithGeolocation();
  updateReshopMessage(isDomestic);
};

// autorun rely on mobx not react for updates & also has a cleanup function (which is returned ), initializing an obveser every render is bad for several reasons (race conditions, memory leaks, etc.)
// docs https://mobx.js.org/reactions.html#always-dispose-of-reactions
autorun(() => {
  if (ShippingCountryState?.supportedCountries) {
    updateCartShippingMessagesWithGeolocation();
  }
});

const CartPage = () => {
  const cartData = useCartData();
  const { subtotal, totalSavings, regularItems, totalCartItemsQty } = cartData;
  const isDomestic = useCheckIfUserIsInUS();

  useEffect(() => {
    updateCartShippingMessages(isDomestic);
    const shippingCopyElement = document.querySelector('#shoppingBag .subtotal__wrapper .cart__total--shipping');
    if (shippingCopyElement) {
      if (!isDomestic) {
        shippingCopyElement.innerText = 'Calculated at Checkout';
      } else {
        shippingCopyElement.innerText = 'Free';
      }
    }
  }, [isDomestic]);

  // Sticky Checkout w/ Fewer CTAs on Mobile Web: A/B test
  useEffect(() => {
    const shouldHideTopStaticCheckoutButton = checkIfInVariation({
      variationId: '29954760014',
      experimentId: '29910610272',
    });
    const topCheckoutButton = document.querySelector('.shopping-bag-content .nested-button');
    if (topCheckoutButton) {
      if (shouldHideTopStaticCheckoutButton) {
        topCheckoutButton.classList.add('d-none');
      } else {
        topCheckoutButton.classList.remove('d-none');
      }
    }
  }, []);

  useEffect(() => {
    if (subtotal === 0 && (!regularItems || regularItems.length === 0)) {
      document.body.classList.add('disable-checkout-mode');
    } else {
      document.body.classList.remove('disable-checkout-mode');
    }
  }, [subtotal, regularItems]);

  // ? [REVISIT] there are several oddities in the code below (in the original code)
  // TT-1291: For Gift Message Option
  // - why run these functions inside of a render function of another component?
  // - why not in isolation? they seem to render non conditionally
  // - why dont have a useEffect hook?
  // - for encapsulation use custom hook
  // leaving some of this as "bad" to keep this closer to what was QA-ed
  useEffect(() => {
    if (shouldRenderUpsellCarousel) lazyRenderCartUpsell();
  }, []);
  // checking "cart_notes_enable" key in "ServerCartData" (FILE: /shopify-src/config/settings_schema.json)
  const cartNoteEnabled = serverCartData?.settings.cart_notes_enable;
  // if all items in cart are giftcards then don't render GiftMessage
  const hasOnlyGiftCard = cartData.regularItems.every((cartItem) => cartItem.gift_card === true);
  useEffect(() => {
    if (!cartNoteEnabled || hasOnlyGiftCard) {
      if (!cartNoteEnabled) {
        console.warn('cartNoteEnabled is: ', cartNoteEnabled);
      }
    } else {
      // bad idea to run render multiple times on same mountnode, w/o doing cleanup
      // which begs the question why isn't the logic encapsulated in that component/module?
      lazyRenderGiftMessage();
    }
  }, [cartNoteEnabled, hasOnlyGiftCard]);

  return (
    <>
      <Subtotals subtotal={subtotal} />
      <Savings savings={totalSavings} />
      <CartCounts count={totalCartItemsQty} />
      <LabeledCartCounts count={totalCartItemsQty} />
      <ShoppingBag cartData={cartData} />
      <GWPCarousel cartData={cartData} />
    </>
  );
};

export default CartPage;

const serverCartData: WindowExtended.ServerCartData | undefined = window.server_cart_data;

// TT-1291: For Gift Message Option
const lazyRenderGiftMessage = () => {
  import(
    /* webpackChunkName: "renderGiftMessageContainerLazy" */
    './GiftMessage/renderGiftMessageContainerLazy'
  ).then((module) => module.renderGiftMessageContainer());
};

// ShoppingBag sub-components to render the cartline items
const ShoppingBag = ({ cartData }: { cartData: CartData }) => {
  useEffect(() => {
    renderComponent({ cartData });
  }, [cartData]);

  const renderComponent = ({ cartData }: { cartData: CartData }) => {
    const mountNodes = Array.from(document.querySelectorAll('[react-render-target="cart-shopping-bag-cart-items"]'));
    mountNodes.forEach((node) => {
      ReactDOM.render(
        <ErrorBoundary>
          <ApolloProvider client={CartApolloClient}>
            <React.Fragment key="cart-shopping-bag">
              <CartItems cartData={cartData} />
            </React.Fragment>
          </ApolloProvider>
        </ErrorBoundary>,
        node
      ); // Render the new component
    });
  };
  return <></>;
};

const GWPCarousel = ({ cartData }: { cartData: CartData }) => {
  useEffect(() => {
    renderComponent();
  }, [cartData]);

  const renderComponent = () => {
    const mountNodes = Array.from(document.querySelectorAll('[react-render-target="gwp-container"]'));
    const UI = () => (
      <ErrorBoundary>
        <ApolloProvider client={CartApolloClient}>
          <Gifts />
        </ApolloProvider>
      </ErrorBoundary>
    );
    mountNodes.forEach((node) => {
      // Render the new component
      ReactDOM.render(<UI />, node);
    });
  };
  return <></>;
};

const CartItems = ({ cartData }: { cartData: CartData }) => {
  const { giftItemsAndLoyaltyRewards, checkoutUrl } = cartData;
  const isCartDataFetchingInprogress = checkoutUrl ? true : false;
  const cartItemGroupsToRender = computeCartItemGroups(cartData);
  const isDesktop = useMediaQuery('(min-width: 992px)');

  useEffect(() => {
    fireGA4cartViewEvent(cartData.regularItems);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isCartDataFetchingInprogress) hideLoadingSpinner();
  }, [isCartDataFetchingInprogress]);

  const [isMoveProductToWishlistOnLoadFired, setFired] = useState(false);
  useEffect(() => {
    const productAddToWishlistSourceOnLoad = InitialUrlSearchParams?.get('addToWishlistSource');
    const moveProductToWishlistOnLoad = async () => {
      const productIdToMoveOnLoad = InitialUrlSearchParams?.get('addToWishlist');
      if (!productIdToMoveOnLoad) return;
      setFired(true); // Make sure it fires only once and keep all vars in deps array
      const wishlistAddedResponse = await addItemToWishlistFromURLparam({
        source: 'Cart',
        'Wishlist Items Count': wishlistState.productIds.length + 1,
      });
      if (!wishlistAddedResponse) return;
      const isItemInWishlist = wishlistAddedResponse.ok || wishlistAddedResponse.alreadyAdded;
      const cartItemGroups = cartItemGroupsToRender.filter((group) => !isArray(group));
      const key = (cartItemGroups as CartItem[]).find((group) => group.product_id === Number(productIdToMoveOnLoad))
        ?.key;
      if (key && isItemInWishlist) changeItemQuantity(key, 0);
    };
    if (!isMoveProductToWishlistOnLoadFired && productAddToWishlistSourceOnLoad === 'Cart')
      moveProductToWishlistOnLoad();
  }, [isMoveProductToWishlistOnLoadFired, cartItemGroupsToRender]);

  const { data, isLoading } = usePromise(() => GWPandLoyaltyGeoSettings());
  const redeemButtonText = data?.redeemButtonText || undefined;
  const customSubtitleBeforePoints = data?.customSubtitleBeforePoints;

  return (
    <div
      style={!serverCartData?.settings.enable_cart_upselling_block && isDesktop ? { marginBottom: 50 } : {}}
      className="cart-items cart-page"
    >
      {cartItemGroupsToRender.length === 0 && (
        <div className="paid-items-reminder">
          {giftItemsAndLoyaltyRewards.length === 0 ? (
            <p>Your bag is empty.</p>
          ) : (
            <p>Gifts are redeemable within your cart, along with at least one product for purchase.</p>
          )}
          <a href="/collections/new-arrivals" className="btn btn-primary mt-2">
            Shop Now
          </a>
        </div>
      )}
      {cartItemGroupsToRender.map((group) => {
        if (!isArray(group)) {
          const item = group;
          const { key, product_title, price, url, quantity, sku, tags } = item;
          const onlineSalePrice = tags ? getOnlineSalePricing(tags, price) : undefined;
          const priceToRender = onlineSalePrice ? onlineSalePrice * 100 : price;
          const { variant_title, product_id, title, product_type, displayPrice } = item;
          const strikeThroughPrice = getStrikeThroughLinePrice(item);
          const priceInCents = price && price / 100;
          const displayPriceInCents = displayPrice && displayPrice / 100;
          const wishListGA4ProductData = {
            source: 'cart',
            variantTitle: variant_title,
            id: product_id.toString(),
            title: title,
            productType: product_type,
            price: priceInCents,
            displayPrice: displayPriceInCents,
            discount: displayPriceInCents && priceInCents && displayPriceInCents - priceInCents,
          };
          return (
            <CartItemShell
              item={item}
              quantity={quantity}
              key={key}
              title={product_title}
              href={url}
              price={priceToRender * quantity}
              strikeThroughPrice={strikeThroughPrice}
            >
              <VariantDisplayUI
                item={item}
                hideQuantity={sku === ALO_MOVES_REAL_SKU || isGiftCardWithRecipient(item)}
                extraActions={
                  <React.Fragment>
                    {!isGiftCardWithRecipient(item) ? (
                      <WishlistButtonCart
                        item={wishListGA4ProductData}
                        onClickCallback={() => {
                          changeItemQuantity(key, 0);
                          heapTrack('Web - Wishlist - Add', {
                            isSignedIn: checkIfCustomer(),
                            productId: item.product_id,
                            source: 'Cart',
                            'Wishlist Items Count': wishlistState.productIds.length + 1,
                          });
                        }}
                      />
                    ) : (
                      <span></span>
                    )}
                    <RemoveFromCartButton
                      onClick={async () => {
                        await changeItemQuantity(key, 0);
                      }}
                    />
                  </React.Fragment>
                }
              />
            </CartItemShell>
          );
        } else {
          //@ts-expect-error
          const properties: SetLineItemProperties = group[0].properties;
          const { _setName, _setUid, _setUrl, _setStrikeThroughPrice } = properties;
          let [price, displayPrice] = group.reduce(
            ([priceSum, displayPriceSum], item) => {
              const { quantity, price, displayPrice = price, tags, original_price, amountPerQuantity } = item;
              const onlineSalePrice = tags ? getOnlineSalePricing(tags, price) : undefined;
              const currentDisplayPrice = onlineSalePrice ? original_price : displayPrice;
              return onlineSalePrice
                ? [
                    priceSum + showSingleQuantityAmount(quantity, amountPerQuantity, onlineSalePrice * 100 * quantity),
                    displayPriceSum + currentDisplayPrice * quantity,
                  ]
                : [
                    priceSum + showSingleQuantityAmount(quantity, amountPerQuantity, price * quantity),
                    displayPriceSum + displayPrice * quantity,
                  ];
            },
            [0, 0]
          );
          if (_setStrikeThroughPrice) displayPrice = _setStrikeThroughPrice;
          const groupDataForUI = properties._setForcedMatchingSets ? computeCartGroupData(group) : group;
          const variantsList = groupDataForUI.map((setItem) => setItem.id);
          return (
            <CartItemShell
              className="set-cart-item"
              key={_setUid}
              title={_setName}
              price={price}
              href={_setUrl + '?variants=' + variantsList.join(',')}
              strikeThroughPrice={price === displayPrice ? null : displayPrice}
            >
              {groupDataForUI.map((setItem, index, arr) => (
                <VariantDisplayUI
                  key={setItem.key}
                  item={setItem}
                  hideQuantity={true}
                  hidePrice={true}
                  extraActions={
                    index === arr.length - 1 ? (
                      <RemoveFromCartButton
                        onClick={async () => {
                          await removeLineItems(group.map((item) => item.key));
                        }}
                      />
                    ) : (
                      <span></span>
                    )
                  }
                />
              ))}
            </CartItemShell>
          );
        }
      })}
      <>
        {giftItemsAndLoyaltyRewards.length > 0 &&
          giftItemsAndLoyaltyRewards.map((item) => {
            const { key, product_title, variant_id, final_line_price, properties } = item;
            const isAloMoves30GWP = item.sku === ALO_MOVES_30_GWP;
            return (
              <CartItemShell
                key={key}
                title={product_title}
                href={undefined}
                price={final_line_price}
                strikeThroughPrice={null}
                isGift={true}
                isBeautyGift={false}
                isAloMoves30GWP={isAloMoves30GWP}
              >
                <CosmeticCartItem
                  key={key}
                  item={item}
                  extraActions={
                    <>
                      <span style={{ flex: 1 }}></span>
                      <RemoveFromCartButton
                        onClick={async () => {
                          memorizeGiftRemoval(variant_id);
                          if (item.properties?._loyaltyClaimRewardId) {
                            await refundClaimedReward(
                              item.properties?._loyaltyClaimRewardId,
                              item.variant_id.toString(),
                              key
                            );
                          } else {
                            await changeItemQuantity(key, 0, false, properties);
                            if (properties._isGWP)
                              fireHeapRemovalGWPevent({
                                productTitle: product_title,
                                variantId: variant_id.toString(),
                              });
                          }
                        }}
                      />
                    </>
                  }
                />
              </CartItemShell>
            );
          })}

        {!isLoading && (data?.shouldRenderLoyaltyForDomestic || data?.shouldRenderLoyaltyWidgetForINTL) ? (
          <Rewards
            shouldHideLLwidget={data?.shouldHideWidget}
            redeemButtonText={redeemButtonText}
            customSubtitleBeforePoints={customSubtitleBeforePoints}
          />
        ) : null}
      </>
    </div>
  );
};

const CartItemShell = ({
  item,
  quantity,
  title,
  price,
  strikeThroughPrice,
  isGift = false,
  href,
  children,
  className = '',
  isBeautyGift,
  isAloMoves30GWP,
}: {
  item?: CartItem;
  quantity?: number;
  title: string;
  price: number;
  strikeThroughPrice: number | null;
  isGift?: boolean;
  isBeautyGift?: boolean;
  isAloMoves30GWP?: boolean;
  href: string | undefined;
  children: ReactNode;
  className?: string;
}) => {
  const { displayPrice, amountPerQuantity } = item || {};
  const currentStrikeThroughPrice = displayPrice && quantity ? displayPrice * quantity : strikeThroughPrice;

  return (
    <div
      style={{ order: isAloMoves30GWP ? 1 : 'unset' }}
      className={`CartItemShell cart-item ${className} ${
        isGift ? `cart-gift-item` : isAloMoves30GWP ? 'cart-alo-moves-free-30-item' : ''
      }`}
    >
      <div className="shopping-bag-item shopping-bag-item__new ">
        <div className="flex__wrapper--row d-lg-none p-l-0 p-r-0">
          {href && !isGift ? (
            <a href={href}>
              <p className="cart-item-title body semibold">{title}</p>
            </a>
          ) : (
            <p className="cart-item-title body semibold">{title}</p>
          )}
          <TotalQtyPrice
            className={'text-right p-r-0'}
            price={showSingleQuantityAmount(quantity, amountPerQuantity, price)}
            strikeThroughPrice={currentStrikeThroughPrice}
          />
        </div>
        {children}
      </div>
    </div>
  );
};

let VariantDisplayUI = ({
  item,
  hideQuantity = false,
  hidePrice = false,
  extraActions,
  className = '',
}: {
  item: CartItem;
  hideQuantity?: boolean;
  hidePrice?: boolean;
  extraActions?: ReactNode;
  className?: string;
}) => {
  const {
    gift_card,
    image,
    key,
    options_with_values,
    price,
    product_title,
    product_type,
    quantity,
    showFinalSaleTreatment,
    title,
    url,
    showExchangeOnlyMessage,
    quantityAvailable,
    tags,
    estimatedShipStartDate,
    displayPrice,
    amountPerQuantity,
  } = item;
  const unlimitedInventory = tags ? gift_card || !checkIfItemCanBeSoldOut(tags) : false;
  const onlineSalePrice = tags ? getOnlineSalePricing(tags, price) : undefined;
  const totalQtyPrice = onlineSalePrice ? onlineSalePrice * 100 * quantity : price * quantity;
  const strikeThroughPrice = displayPrice ? displayPrice * quantity : getStrikeThroughLinePrice(item);
  const hasInternationalRestrictions = tags?.includes(INTERNATIONAL_RESTRICTIONS_TAG);
  const enableLineItemSaleMessaging =
    serverCartData?.settings.enable_cart_sale_messaging &&
    !tagsIncludesTag(tags ?? [], serverCartData?.settings.cart_sale_messaging_exclusion_tag);

  const purchaseLimitTagData = extractPurchaseLimitTagData(tags);
  const userIsInUS = useCheckIfUserIsInUS();
  const preorderCopy = preorderDateRangeCopy(estimatedShipStartDate);
  const shouldRenderPreorderMessaging = isEligibleForPreorder(quantity, item.tags, estimatedShipStartDate);

  return (
    <div className={`VariantDisplayUI ${className}`}>
      <div className="item-img item-img__new">
        <a href={url}>
          <img src={image} alt={title} />
        </a>
      </div>
      <div className="item-descr">
        <div className="item-sections">
          <div className="item product-option-messages">
            <a className="d-none d-lg-block product_title--link" href={url}>
              <p className="body-large semibold">{product_title}</p>
            </a>
            <>
              {isGiftCardWithRecipient(item) ? (
                <div>
                  {item.properties['Recipient name'] && (
                    <p className="product-option body-small d-block">
                      <span className="product-option--label">Recipient's name: </span>
                      <span className="product-option--value">{item.properties['Recipient name']}</span>
                    </p>
                  )}
                  {item.properties['Recipient email'] && (
                    <p className="product-option body-small d-block">
                      <span className="product-option--label">Recipient's email: </span>
                      <span className="product-option--value">{item.properties['Recipient email']}</span>
                    </p>
                  )}
                  {item.properties['Message'] && (
                    <p className="product-option gift-card-message body-small d-block">
                      <span className="product-option--label">Message: </span>
                      <span className="product-option--value">{item.properties['Message']}</span>
                    </p>
                  )}
                  {item.properties['Send on'] && (
                    <p className="product-option body-small d-block">
                      <span className="product-option--label">Send date: </span>
                      <span className="product-option--value">
                        {formatDateWithLocaleOptions(item.properties['Send on'])}
                      </span>
                    </p>
                  )}
                </div>
              ) : (
                computeDisplayForOptionWithValues({
                  options: options_with_values,
                  productType: product_type,
                  tags,
                }).map(({ label, value }) => (
                  <p key={label} className="product-option body-small d-block">
                    <span className="product_option--label">{label}:</span>{' '}
                    <span className="product_option--value">{value}</span>
                  </p>
                ))
              )}
              {enableLineItemSaleMessaging && (
                <p className="line-item-copy body-small green">
                  {serverCartData?.settings.cart_sale_messaging_line_item_copy}
                </p>
              )}
              {shouldRenderPreorderMessaging && (
                <p className="line-item-copy body-small red">
                  Preorder expected to ship by: <span>{preorderCopy}</span>
                </p>
              )}
              {!unlimitedInventory && (
                <UrgencyMessage quantityLeft={quantityAvailable} className="cartPage body-small red semibold" />
              )}
              {showFinalSaleTreatment ? (
                <div className="finalSale body-small red">FINAL SALE: This item cannot be returned or exchanged.</div>
              ) : null}
              {userIsInUS && showExchangeOnlyMessage ? (
                <div className="finalSale body-small red">EXCHANGE ONLY: This item cannot be returned.</div>
              ) : null}
              {hasInternationalRestrictions ? (
                <div>
                  <a
                    className="international-restrictions legal red"
                    href="/pages/frequently-asked-questions?question=e-gift-restrictions"
                    target="_blank"
                  >
                    International restrictions apply.
                    <span className="alo-info-icon"></span>
                  </a>
                </div>
              ) : null}
              {purchaseLimitTagData ? (
                <PurchaseLimitMessaging purchaseLimitTagData={purchaseLimitTagData} messageType={'default'} />
              ) : null}
            </>
          </div>
          <div className="quantity-buttons item text-right d-none d-lg-flex pr-0">
            {!hideQuantity ? (
              <QuantityChangeButtons
                buttonClassName={'d-none d-lg-block'}
                isUnlimited={unlimitedInventory}
                itemKey={key}
                quantity={quantity}
                purchaseLimitTagData={purchaseLimitTagData}
                quantityAvailable={quantityAvailable}
              />
            ) : null}
          </div>
          {!hidePrice && (
            <TotalQtyPrice
              className={'text-sm-right d-none pl-0 d-lg-flex'}
              price={showSingleQuantityAmount(quantity, amountPerQuantity, totalQtyPrice)}
              strikeThroughPrice={strikeThroughPrice}
            />
          )}
        </div>
        {!hideQuantity && (
          <QuantityChangeButtons
            isUnlimited={unlimitedInventory}
            itemKey={key}
            quantity={quantity}
            wrapperClassName={'d-lg-none'}
            purchaseLimitTagData={purchaseLimitTagData}
            quantityAvailable={quantityAvailable}
          />
        )}
        <div className={'item__buttons'}>{extraActions}</div>
      </div>
    </div>
  );
};
VariantDisplayUI = observer(VariantDisplayUI);

export const CosmeticCartItem = ({
  item,
  extraActions,
  isMiniBag,
}: {
  item: ShopifyCartItem;
  extraActions: ReactNode;
  isMiniBag?: boolean;
}) => {
  const {
    product_title,
    product_description,
    image,
    title,
    final_line_price,
    options_with_values,
    product_type,
  } = item;
  return (
    <div className={`VariantDisplayUI`}>
      <div className="item-img item-img__new">
        <img src={image} alt={title} />
      </div>
      <div className="item-descr">
        <div className="item-sections">
          <div className="item product-option-messages">
            {!isMiniBag ? <p className={`body-large semibold ${'d-none d-lg-block'}`}>{product_title}</p> : ''}
            <p>
              {computeDisplayForOptionWithValues({
                options: options_with_values,
                productType: product_type,
              }).map(({ label, value }) => (
                <p key={label} className="product-option body-small black d-block">
                  <span className="product-option__label">{label}: </span>
                  <span className="product-option__value">{value}</span>
                </p>
              ))}
            </p>
            <p className="body-small product-description">{product_description}</p>
          </div>
          {/* empty div for grid layout */}
          <div></div>
          {!isMiniBag ? (
            <TotalQtyPrice
              className={'text-sm-right d-none pl-0 d-lg-block'}
              price={final_line_price}
              strikeThroughPrice={null}
            />
          ) : (
            ''
          )}
        </div>
        <div className="item__buttons justify-content-between">{extraActions}</div>
      </div>
    </div>
  );
};

// helper functions / sub-components/portals
const Subtotals = ({ subtotal }: { subtotal: number }) => {
  useEffect(() => {
    renderComponent({ subtotal });
  }, [subtotal]);

  const renderComponent = ({ subtotal }: { subtotal: number }) => {
    const mountNodes = Array.from(document.querySelectorAll('[react-render-target="cart-subtotal-formatted"]'));
    const UI = () => (
      <ApolloProvider client={CartApolloClient}>
        <React.Fragment key="cart-shopping-bag">{formatShopifyPrice(subtotal)}</React.Fragment>
      </ApolloProvider>
    );
    mountNodes.forEach((node) => {
      ReactDOM.render(
        <ErrorBoundary>
          <UI />
        </ErrorBoundary>,
        node
      ); // Render the new component
    });
  };
  return <></>;
};

// Subtotals = observer(Subtotals);
const Savings = ({ savings }: { savings: number }) => {
  useEffect(() => {
    renderComponent({ savings });
  }, [savings]);

  const renderComponent = ({ savings }: { savings: number }) => {
    if (savings === 0) return null;
    const mountNodes = Array.from(document.querySelectorAll('[react-render-target="cart-savings-formatted"]'));
    const formattedPrice = formatShopifyPrice(savings);
    const UI = () => (
      <ErrorBoundary>
        <ApolloProvider client={CartApolloClient}>
          You're saving <span className="urgency__notice--savings">{formattedPrice}</span> on this order!
        </ApolloProvider>
      </ErrorBoundary>
    );
    mountNodes.forEach((node) => {
      ReactDOM.render(<UI />, node); // Render the new component
    });
  };
  return <></>;
};
// Savings = observer(Savings);

const CartCounts = ({ count }: { count: number }) => {
  useEffect(() => {
    renderComponent({ count });
  }, [count]);
  const renderComponent = ({ count }: { count: number }) => {
    const mountNodes = Array.from(document.querySelectorAll('[react-render-target="cart-items-count"]'));
    const UI = () => <ApolloProvider client={CartApolloClient}>{count}</ApolloProvider>;
    mountNodes.forEach((node) => {
      ReactDOM.render(
        <ErrorBoundary>
          <UI />
        </ErrorBoundary>,
        node
      ); // Render the new component
    });
  };
  return <></>;
};

const LabeledCartCounts = ({ count }: { count: number }) => {
  useEffect(() => {
    renderComponent({ count });
  }, [count]);
  const renderComponent = ({ count }: { count: number }) => {
    const mountNodes = Array.from(document.querySelectorAll('[react-render-target="cart-items-count-with-label"]'));
    const UI = () => <span className="cart-count-text">{pluralize(count, 'Item')}</span>;
    mountNodes.forEach((node) => {
      ReactDOM.render(
        <ErrorBoundary>
          <UI />
        </ErrorBoundary>,
        node
      ); // Render the new component
    });
  };
  return <></>;
};

const lazyRenderCartUpsell = () => {
  if (shouldRenderUpsellCarousel) {
    import(
      /* webpackChunkName: "renderCartUpsellBlocksLazy" */
      './CartImpulseBuy/renderCartUpsellBlocksLazy'
    ).then((module) => {
      module.renderCartUpsellBlocks();
    });
  }
};

export const TotalQtyPrice = observer(
  ({
    className = '',
    price,
    strikeThroughPrice,
  }: {
    className?: string;
    price: number;
    strikeThroughPrice: number | null;
  }) => (
    <div className={`price__wrapper--new ${className}`}>
      {price === 0 ? (
        <span className="price">
          <span className="h6 uppercase">Free</span>
        </span>
      ) : strikeThroughPrice ? (
        <React.Fragment>
          <span
            className={`price h6 red ${
              ShippingCountryState.countryCode === 'US' ? 'price-display-inline' : 'price-display'
            }`}
          >
            <span data-currency>{formatShopifyPrice(price)}</span>
          </span>
          <span className={'h6 currency-formatting '}>
            <span className="h6 strikethrough">{formatShopifyPrice(strikeThroughPrice)}</span>
          </span>
        </React.Fragment>
      ) : (
        <span className="price h6">
          <span data-currency>{formatShopifyPrice(price)}</span>
        </span>
      )}
    </div>
  )
);

const hideLoadingSpinner = () => {
  const cartItemsContainer = document.getElementById('shoppingBag');
  cartItemsContainer?.classList.remove('loading');
};
